import { useEffect, useMemo, useRef, useState } from 'react';
import Map, {
  Layer,
  Marker,
  NavigationControl,
  Source,
  type MapRef,
} from 'react-map-gl/maplibre';
import type { StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Play, Pause, SkipBack, Navigation } from 'lucide-react';
import { cn, formatDateTime, formatKnots } from '@/lib/utils';
import type { Position } from '@/api/types';

const STYLE: StyleSpecification = {
  version: 8,
  sources: {
    base: {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
      ],
      tileSize: 256,
      attribution: '© CARTO · © OpenStreetMap',
    },
  },
  layers: [{ id: 'base', type: 'raster', source: 'base' }],
};

const STEP_MS = 250; // tempo entre pontos no replay
const SPEEDS = [1, 2, 4, 8];

interface Pt {
  lon: number;
  lat: number;
  fixTime: string;
  speed?: number;
  course?: number;
}

export function RouteReplayMap({ positions }: { positions: Position[] }) {
  const mapRef = useRef<MapRef | null>(null);

  const pts = useMemo<Pt[]>(
    () =>
      positions
        .filter((p) => p.latitude != null && p.longitude != null)
        .map((p) => ({
          lon: p.longitude!,
          lat: p.latitude!,
          fixTime: p.fixTime,
          speed: p.speed,
          course: p.course,
        })),
    [positions],
  );

  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(2);

  // reinicia quando a rota muda
  useEffect(() => {
    setIdx(0);
    setPlaying(false);
  }, [pts]);

  // animação do replay
  useEffect(() => {
    if (!playing || pts.length < 2) return;
    const t = setInterval(() => {
      setIdx((i) => {
        if (i >= pts.length - 1) {
          return i; // chegou ao fim
        }
        return i + 1;
      });
    }, STEP_MS / rate);
    return () => clearInterval(t);
  }, [playing, rate, pts.length]);

  // pausa ao chegar no fim
  useEffect(() => {
    if (idx >= pts.length - 1) setPlaying(false);
  }, [idx, pts.length]);

  const lineGeoJson = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          properties: {},
          geometry: { type: 'LineString' as const, coordinates: pts.map((p) => [p.lon, p.lat]) },
        },
      ],
    }),
    [pts],
  );

  // percorrido até o ponto atual (linha destacada)
  const traveledGeoJson = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: pts.slice(0, idx + 1).map((p) => [p.lon, p.lat]),
          },
        },
      ],
    }),
    [pts, idx],
  );

  const initialView = useMemo(() => {
    if (pts.length === 0) return { longitude: -43.16, latitude: -22.9, zoom: 10 };
    const lon = pts.reduce((s, p) => s + p.lon, 0) / pts.length;
    const lat = pts.reduce((s, p) => s + p.lat, 0) / pts.length;
    return { longitude: lon, latitude: lat, zoom: 12 };
  }, [pts]);

  // fit bounds quando a rota carrega
  useEffect(() => {
    if (pts.length < 2 || !mapRef.current) return;
    let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
    for (const p of pts) {
      minLon = Math.min(minLon, p.lon); maxLon = Math.max(maxLon, p.lon);
      minLat = Math.min(minLat, p.lat); maxLat = Math.max(maxLat, p.lat);
    }
    mapRef.current.fitBounds([[minLon, minLat], [maxLon, maxLat]], { padding: 50, duration: 600, maxZoom: 15 });
  }, [pts]);

  if (pts.length === 0) return null;

  const cur = pts[Math.min(idx, pts.length - 1)];

  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <div className="relative h-[380px]">
        <Map
          ref={mapRef}
          initialViewState={initialView}
          mapStyle={STYLE}
          style={{ width: '100%', height: '100%' }}
          attributionControl={false}
        >
          <NavigationControl position="top-right" showCompass={false} />

          {/* overlay náutico OpenSeaMap */}
          <Source
            id="osm-seamark"
            type="raster"
            tiles={['https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png']}
            tileSize={256}
          >
            <Layer id="osm-seamark-l" type="raster" paint={{ 'raster-opacity': 0.9 }} />
          </Source>

          {/* rota completa (cinza) + percorrido (azul) */}
          <Source id="route" type="geojson" data={lineGeoJson}>
            <Layer id="route-l" type="line" paint={{ 'line-color': '#8E8E93', 'line-width': 3, 'line-opacity': 0.6 }} />
          </Source>
          <Source id="traveled" type="geojson" data={traveledGeoJson}>
            <Layer id="traveled-l" type="line" paint={{ 'line-color': '#007AFF', 'line-width': 4 }} />
          </Source>

          {/* início / fim */}
          <Marker longitude={pts[0].lon} latitude={pts[0].lat} anchor="center">
            <span className="block h-3 w-3 rounded-full bg-status-running ring-2 ring-white" title="Início" />
          </Marker>
          <Marker longitude={pts[pts.length - 1].lon} latitude={pts[pts.length - 1].lat} anchor="center">
            <span className="block h-3 w-3 rounded-full bg-status-blocked ring-2 ring-white" title="Fim" />
          </Marker>

          {/* embarcação atual */}
          <Marker longitude={cur.lon} latitude={cur.lat} anchor="center">
            <span
              className="flex h-7 w-7 items-center justify-center rounded-full bg-brand text-white shadow-md ring-2 ring-white"
              style={{ transform: `rotate(${cur.course ?? 0}deg)` }}
            >
              <Navigation className="h-4 w-4" />
            </span>
          </Marker>
        </Map>
      </div>

      {/* controles de replay */}
      <div className="flex flex-wrap items-center gap-3 border-t border-border bg-card p-3">
        <button
          type="button"
          onClick={() => { if (idx >= pts.length - 1) setIdx(0); setPlaying((p) => !p); }}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          title={playing ? 'Pausar' : 'Reproduzir'}
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={() => { setIdx(0); setPlaying(false); }}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-secondary hover:bg-accent"
          title="Reiniciar"
        >
          <SkipBack className="h-4 w-4" />
        </button>

        <input
          type="range"
          min={0}
          max={pts.length - 1}
          value={idx}
          onChange={(e) => { setIdx(Number(e.target.value)); setPlaying(false); }}
          className="h-1.5 flex-1 cursor-pointer accent-primary"
        />

        <div className="flex gap-1">
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setRate(s)}
              className={cn(
                'rounded-md px-2 py-1 text-[11px] font-bold',
                rate === s ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:bg-accent',
              )}
            >
              {s}x
            </button>
          ))}
        </div>
      </div>

      {/* leitura do ponto atual */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-secondary/40 px-3 py-2 text-xs">
        <span className="font-semibold">
          Ponto {idx + 1}/{pts.length}
        </span>
        <span className="text-muted-foreground">{formatDateTime(cur.fixTime)}</span>
        <span className="font-semibold">{formatKnots(cur.speed)}</span>
        {cur.course != null && <span className="text-muted-foreground">Rumo {Math.round(cur.course)}°</span>}
      </div>
    </div>
  );
}
